import { Card } from '@ui/themed';
import { t } from 'i18next';
import { B2BFlowDiagram } from '../../../components/b2b-flow-diagram';
import {
  EmptyStateCtaCard,
  EmptyStateHero,
  HeroPreviewCard,
} from '../../../components/empty-state-hero';
import { isShopify, useEnv } from '../../../context/env-context';
import { useHandlers } from '../../../context/page-context';
import { cn } from '../../../lib/styles';
import {
  CUSTOMER_GROUP_CREATE_METHOD_PATH_IDS,
  useLastCustomerGroupCreateMethod,
} from '../create-method';
import { getPlatformCustomersLink } from '../hooks/use-customer-group-tag';

// Mock of a customer group's ordering rules
const PREVIEW_HEADER = { name: 'VIP wholesale', members: '12 customers' };
const PREVIEW_ROWS = [
  { label: 'Price list', value: 'Wholesale −50%' },
  { label: 'Payment', value: 'Invoice' },
  { label: 'Min order', value: '$500' },
];

export function CustomerGroupEmptyState() {
  const { navigationHandler } = useHandlers();
  const env = useEnv();
  const shopify = isShopify(env.platform);
  const [lastCustomerGroupCreateMethod] = useLastCustomerGroupCreateMethod();

  // Customers are tagged on the platform itself, so link there when we can
  const platformCustomersLink = getPlatformCustomersLink(env.connected_platform, env.store_id);

  return (
    <Card hidePadding>
      <div className="flex flex-col overflow-hidden rounded-xl">
        <EmptyStateHero
          eyebrow={t('features:customer_group.components.customer_group_empty_state.eyebrow')}
          title={t('features:customer_group.components.customer_group_empty_state.title')}
          description={
            shopify
              ? t(
                  'features:customer_group.components.customer_group_empty_state.description_shopify',
                )
              : t('features:customer_group.components.customer_group_empty_state.description')
          }
        >
          <GroupPreviewCard />
        </EmptyStateHero>

        <div className="hidden border-gray-200 border-b px-10 py-6 min-[1200px]:block">
          <B2BFlowDiagram shopify={shopify} highlightStep="customerGroup" />
        </div>

        <div className="grid gap-6 px-6 pt-6 pb-6 sm:px-10 sm:pb-10 lg:grid-cols-2">
          <EmptyStateCtaCard
            highlighted
            badge={t('features:customer_group.components.customer_group_empty_state.create.badge')}
            title={t('features:customer_group.components.customer_group_empty_state.create.title')}
            description={t(
              'features:customer_group.components.customer_group_empty_state.create.description',
            )}
            cta={t('features:customer_group.components.customer_group_empty_state.create.cta')}
            onClick={() =>
              navigationHandler.navigateById(
                CUSTOMER_GROUP_CREATE_METHOD_PATH_IDS[lastCustomerGroupCreateMethod],
              )
            }
          />

          <EmptyStateCtaCard
            title={t(
              'features:customer_group.components.customer_group_empty_state.add_customers.title',
            )}
            description={
              shopify
                ? t(
                    'features:customer_group.components.customer_group_empty_state.add_customers.description_shopify',
                  )
                : t(
                    'features:customer_group.components.customer_group_empty_state.add_customers.description',
                  )
            }
            cta={t(
              'features:customer_group.components.customer_group_empty_state.add_customers.cta',
            )}
            ctaIcon={platformCustomersLink ? 'externalLink' : undefined}
            onClick={() => {
              if (platformCustomersLink) {
                window.open(platformCustomersLink, '_blank');
              } else {
                navigationHandler.navigate('/customers/list');
              }
            }}
          />
        </div>
      </div>
    </Card>
  );
}

// Illustration of a group's rule bundle, floating on the hero
function GroupPreviewCard() {
  return (
    <HeroPreviewCard>
      <div className="flex items-center justify-between border-gray-100 border-b px-1 pb-3">
        <span className="font-mono font-semibold text-gray-500 text-xs">{PREVIEW_HEADER.name}</span>
        <span className="font-mono font-semibold text-platform-primary text-xs">
          {PREVIEW_HEADER.members}
        </span>
      </div>

      {PREVIEW_ROWS.map((row, i) => (
        <div
          className={cn(
            'flex items-center justify-between gap-3 px-1 py-3',
            i === PREVIEW_ROWS.length - 1 && 'opacity-50',
          )}
          key={row.label}
        >
          <span className="text-gray-500 text-sm">{row.label}</span>
          <span className="font-semibold text-platform-primary">{row.value}</span>
        </div>
      ))}
    </HeroPreviewCard>
  );
}
